import axios from "axios";
import config from "utils/apiEndPoint";
import {
  EMPLOYEE_SHIPCLUES_SIDEBAR_ITEMS,
  buildPermissionModulesFromCatalog,
} from "configs/employee.sidebar.permission.modules";
import { decodeJwtPayload } from "utils/authOtpUtils";

const API_V1_BASE = "https://api.shipclues.in/api/v1";

function resolveEmployeePermissionsUrl(forAxiosInstance = false) {
  const path = config.employees.employeePermissions;
  if (path.startsWith("http")) return path;
  if (forAxiosInstance) return path;
  return `${API_V1_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

function truthyFlag(value) {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    String(value).toLowerCase() === "true"
  );
}

/**
 * Unwrap permission rows from assorted API payload shapes.
 */
export function extractPermissionListFromPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.permissions)) return payload.permissions;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.data?.permissions)) return payload.data.permissions;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.results?.permissions)) {
    return payload.results.permissions;
  }
  if (payload.data?.byKey && typeof payload.data.byKey === "object") {
    return Object.values(payload.data.byKey);
  }
  if (payload.byKey && typeof payload.byKey === "object") {
    return Object.values(payload.byKey);
  }
  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    const nested = extractPermissionListFromPayload(payload.data);
    if (nested.length > 0) return nested;
  }
  return [];
}

/**
 * Normalize profile / UI-permission rows into Redux sidebar shape.
 */
export function normalizePermissionObjects(list) {
  if (!Array.isArray(list)) return [];
  const out = [];

  list.forEach((item) => {
    if (!item || typeof item !== "object") return;

    if (typeof item === "string" && item.trim()) {
      out.push({
        permission_key: item.trim(),
        can_view: true,
        can_create: false,
        can_update: false,
        module_name: null,
        module_code: null,
      });
      return;
    }

    const key =
      item.permission_key ??
      item.permissionKey ??
      item.key ??
      item.code ??
      item.module_key ??
      item.module ??
      null;
    if (!key) return;

    out.push({
      id: item.id ?? null,
      permission_key: String(key),
      can_view: truthyFlag(item.can_view),
      can_create: truthyFlag(item.can_create),
      can_update: truthyFlag(item.can_update),
      module_name: item.module_name || null,
      module_code: item.module_code || null,
    });
  });

  return out;
}

/** Resolve ids used by permission GET APIs from a roster row. */
export function resolveEmployeePermissionIds(row) {
  const raw = row?.original ?? row ?? {};
  const email = String(row?.email ?? raw?.email ?? "").trim();
  const userId =
    raw?.user_id ??
    raw?.user?.id ??
    raw?.user?.pk ??
    (typeof raw.user === "number" ? raw.user : null) ??
    raw?.admin?.id ??
    raw?.admin?.pk;
  const employeeId =
    raw?.admin_employee_id ??
    raw?.employee_id ??
    raw?.id ??
    row?.id ??
    raw?.pk;

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  return {
    email,
    userId: toNum(userId) ?? userId ?? null,
    employeeId: toNum(employeeId) ?? employeeId ?? null,
  };
}

/** Profile `permissions: []` rows — maps all three access flags from the modal. */
export function buildProfilePermissionsFromUiArray(permissionsArray = []) {
  return permissionsArray
    .filter(
      (p) =>
        p &&
        p.permission_key &&
        (p.can_view || p.can_create || p.can_update)
    )
    .map((p) => ({
      permission_key: p.permission_key,
      can_view: !!p.can_view,
      can_create: !!p.can_create,
      can_update: !!p.can_update,
    }));
}

/**
 * Map GET employee-permissions rows → modal `byKey` (stateKey → can_view/create/update).
 */
export function permissionsListToModalByKey(
  list,
  userId,
  sidebarItems = EMPLOYEE_SHIPCLUES_SIDEBAR_ITEMS
) {
  const byPermissionKey = {};
  normalizePermissionObjects(list).forEach((perm) => {
    byPermissionKey[perm.permission_key] = perm;
  });

  const byKey = {};
  sidebarItems.forEach((row) => {
    const stateKey = row.stateKey ?? row.permissionKeyForApi;
    const apiKey = row.permissionKeyForApi ?? stateKey;
    const perm = byPermissionKey[apiKey] || byPermissionKey[stateKey];
    byKey[stateKey] = {
      id: perm?.id ?? null,
      user: userId,
      permission_key: apiKey,
      can_view: !!perm?.can_view,
      can_create: !!perm?.can_create,
      can_update: !!perm?.can_update,
    };
  });
  return byKey;
}

/** Merge saved profile permissions into modal `byKey` map (fills gaps / offline fallback). */
export function mergeProfilePermissionsIntoByKey(
  byKey,
  profilePermissions,
  userId,
  sidebarItems = EMPLOYEE_SHIPCLUES_SIDEBAR_ITEMS
) {
  const next = { ...(byKey || {}) };
  const normalized = normalizePermissionObjects(profilePermissions);

  sidebarItems.forEach((row) => {
    const stateKey = row.stateKey ?? row.permissionKeyForApi;
    const apiKey = row.permissionKeyForApi ?? stateKey;
    const perm = normalized.find(
      (p) => p.permission_key === apiKey || p.permission_key === stateKey
    );
    if (!perm) return;
    const existing = next[stateKey];
    next[stateKey] = {
      id: existing?.id ?? perm.id ?? null,
      user: existing?.user ?? userId,
      permission_key: apiKey,
      can_view: !!perm.can_view,
      can_create: !!perm.can_create,
      can_update: !!perm.can_update,
    };
  });

  normalized.forEach((perm) => {
    const k = perm.permission_key;
    if (!k || next[k]) return;
    next[k] = {
      id: perm.id ?? null,
      user: userId,
      permission_key: k,
      can_view: !!perm.can_view,
      can_create: !!perm.can_create,
      can_update: !!perm.can_update,
    };
  });

  return next;
}

/**
 * Normalize GET `/admins/admin-employee-permissions/` rows.
 * Shape: `{ id, module, code, name, description }`.
 */
export function normalizeAdminEmployeePermissionCatalog(payload) {
  const raw = extractPermissionListFromPayload(payload);
  return raw
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const id = Number(item.id);
      if (!Number.isFinite(id)) return null;
      const moduleSlug = String(item.module || "").trim();
      const code = String(item.code || "view").trim().toLowerCase();
      const name = String(item.name || "").trim();
      return {
        id,
        module: moduleSlug,
        code,
        name,
        description: item.description || "",
        permission_key:
          item.permission_key ||
          (moduleSlug ? `${moduleSlug}.${code}` : String(item.key || id)),
        module_name: name,
        module_code: code,
        can_view: code === "view",
        can_create: code === "create",
        can_update: code === "update",
      };
    })
    .filter(Boolean);
}

/** GET master permission catalog (`/admins/admin-employee-permissions/`). */
export async function fetchEmployeePermissionCatalog(
  axiosGet,
  forAxiosInstance = false
) {
  if (!axiosGet) return [];
  const url = resolveEmployeePermissionsUrl(forAxiosInstance);
  try {
    const res = await axiosGet(url, {});
    const body = res?.data ?? res;
    const catalog = normalizeAdminEmployeePermissionCatalog(body);
    if (catalog.length > 0) return catalog;
  } catch {
    /* try fallback reader */
  }
  const list = await tryPermissionGet(axiosGet, url, {});
  return normalizeAdminEmployeePermissionCatalog({ data: list });
}

/** Index catalog rows by numeric id and permission_key. */
export function indexPermissionCatalog(catalog = []) {
  const byId = new Map();
  const byKey = new Map();
  const byModuleCode = new Map();
  const list = Array.isArray(catalog)
    ? catalog
    : normalizePermissionObjects(catalog);

  list.forEach((perm) => {
    if (perm.id != null && Number.isFinite(Number(perm.id))) {
      byId.set(Number(perm.id), perm);
    }
    if (perm.permission_key) {
      byKey.set(String(perm.permission_key), perm);
    }
    if (perm.module) {
      const mc = `${perm.module}.${perm.code || "view"}`;
      byModuleCode.set(mc, perm);
      byKey.set(mc, perm);
    }
  });
  return { byId, byKey, byModuleCode, list };
}

function invertStubKeyToIdMap(stubKeyToId = {}) {
  const stubIdToKey = {};
  Object.entries(stubKeyToId).forEach(([key, id]) => {
    const n = Number(id);
    if (Number.isFinite(n)) stubIdToKey[n] = key;
  });
  return stubIdToKey;
}

/**
 * Map stub ids (101–108) or keys → real catalog permission ids when available.
 */
export function mapPermissionIdsToCatalogIds(
  rawIds,
  catalog = [],
  stubKeyToId = {}
) {
  if (!Array.isArray(rawIds) || rawIds.length === 0) return [];
  const { byId, byKey } = indexPermissionCatalog(catalog);
  const stubIdToKey = invertStubKeyToIdMap(stubKeyToId);
  const out = new Set();

  rawIds.forEach((raw) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    if (byId.has(n)) {
      out.add(n);
      return;
    }
    const key = stubIdToKey[n];
    if (key && byKey.has(key)) {
      out.add(Number(byKey.get(key).id));
      return;
    }
    out.add(n);
  });

  return [...out].filter((id) => Number.isFinite(id));
}

/**
 * Build modal `byKey` from saved `permission_ids` + catalog modules.
 */
export function permissionIdsToModalByKey(
  permissionIds,
  modulesForModal,
  userId
) {
  const idSet = new Set(
    (permissionIds || [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n))
  );
  const byKey = {};

  (modulesForModal || []).forEach((module) => {
    (module.items || []).forEach((item) => {
      const stateKey = item.stateKey ?? item.key;
      const catalogIds = item.catalogIds || {};
      const can_view =
        catalogIds.view != null && idSet.has(Number(catalogIds.view));
      const can_create =
        catalogIds.create != null && idSet.has(Number(catalogIds.create));
      const can_update =
        catalogIds.update != null && idSet.has(Number(catalogIds.update));

      byKey[stateKey] = {
        id: catalogIds.view ?? catalogIds.create ?? catalogIds.update ?? null,
        user: userId,
        permission_key: item.permissionKeyForApi ?? stateKey,
        catalogIds,
        can_view,
        can_create,
        can_update,
      };
    });
  });

  return byKey;
}

/** Redux sidebar shape from saved `permission_ids` + catalog. */
export function catalogPermissionIdsToSessionPermissions(
  permissionIds,
  catalog = []
) {
  const modules = buildPermissionModulesFromCatalog(catalog);
  const byKey = permissionIdsToModalByKey(permissionIds, modules, null);
  return Object.entries(byKey)
    .filter(
      ([, cell]) =>
        cell.can_view || cell.can_create || cell.can_update
    )
    .map(([stateKey, cell]) => ({
      id: cell.id,
      permission_key: cell.permission_key || stateKey,
      can_view: !!cell.can_view,
      can_create: !!cell.can_create,
      can_update: !!cell.can_update,
      module_name: stateKey,
      module_code: null,
    }));
}

/**
 * Build `permission_ids` for PATCH from modal `byKey` (checked flags → catalog ids).
 */
export function buildPermissionIdsFromModalByKey(
  byKey,
  modulesForModal = null,
  catalog = [],
  stubKeyToId = {}
) {
  if (!byKey || typeof byKey !== "object") return [];
  const ids = new Set();

  const modules =
    modulesForModal && modulesForModal.length > 0
      ? modulesForModal
      : buildPermissionModulesFromCatalog(catalog);

  modules.forEach((module) => {
    (module.items || []).forEach((item) => {
      const stateKey = item.stateKey ?? item.key;
      const cell = byKey[stateKey];
      if (!cell) return;
      const catalogIds = cell.catalogIds ?? item.catalogIds ?? {};
      if (cell.can_view && catalogIds.view != null) {
        ids.add(Number(catalogIds.view));
      }
      if (cell.can_create && catalogIds.create != null) {
        ids.add(Number(catalogIds.create));
      }
      if (cell.can_update && catalogIds.update != null) {
        ids.add(Number(catalogIds.update));
      }
    });
  });

  if (ids.size > 0) {
    return [...ids].filter((n) => Number.isFinite(n));
  }

  const { byKey: catalogByKey } = indexPermissionCatalog(catalog);
  EMPLOYEE_SHIPCLUES_SIDEBAR_ITEMS.forEach((row) => {
    const stateKey = row.stateKey ?? row.permissionKeyForApi;
    const apiKey = row.permissionKeyForApi ?? stateKey;
    const cell = byKey[stateKey];
    if (!cell) return;
    const hasAccess =
      cell.can_view || cell.can_create || cell.can_update;
    if (!hasAccess) return;
    const catalogPerm = catalogByKey.get(apiKey);
    if (catalogPerm?.id != null && Number.isFinite(Number(catalogPerm.id))) {
      ids.add(Number(catalogPerm.id));
      return;
    }
    const stub = stubKeyToId[apiKey];
    if (Number.isFinite(Number(stub))) ids.add(Number(stub));
  });

  return mapPermissionIdsToCatalogIds([...ids], catalog, stubKeyToId);
}

/** Hydrate modal checkboxes from saved `permission_ids`. */
export function mergePermissionIdsIntoModalByKey(
  byKey,
  permissionIds,
  catalog = [],
  stubKeyToId = {},
  modulesForModal = null,
  userId
) {
  const modules =
    modulesForModal && modulesForModal.length > 0
      ? modulesForModal
      : buildPermissionModulesFromCatalog(catalog);
  const fromIds = permissionIdsToModalByKey(
    permissionIds,
    modules,
    userId
  );
  const next = { ...(byKey || {}) };

  Object.entries(fromIds).forEach(([stateKey, cell]) => {
    const existing = next[stateKey];
    next[stateKey] = {
      ...existing,
      ...cell,
      can_view: existing?.can_view || cell.can_view,
      can_create: existing?.can_create || cell.can_create,
      can_update: existing?.can_update || cell.can_update,
      catalogIds: cell.catalogIds ?? existing?.catalogIds,
    };
  });

  if (Object.keys(fromIds).length > 0 || !permissionIds?.length) {
    return next;
  }

  const idSet = new Set(
    (permissionIds || [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n))
  );
  const { byId } = indexPermissionCatalog(catalog);
  const stubIdToKey = invertStubKeyToIdMap(stubKeyToId);

  EMPLOYEE_SHIPCLUES_SIDEBAR_ITEMS.forEach((row) => {
    const stateKey = row.stateKey ?? row.permissionKeyForApi;
    const apiKey = row.permissionKeyForApi ?? stateKey;
    const granted = [...idSet].some((id) => {
      const fromCatalog = byId.get(id);
      return (
        stubIdToKey[id] === apiKey ||
        fromCatalog?.permission_key === apiKey ||
        fromCatalog?.permission_key === stateKey
      );
    });
    if (!granted) return;
    const existing = next[stateKey];
    next[stateKey] = {
      id: existing?.id ?? null,
      user: existing?.user ?? userId,
      permission_key: apiKey,
      can_view: existing?.can_view ?? true,
      can_create: existing?.can_create ?? false,
      can_update: existing?.can_update ?? false,
    };
  });

  return next;
}

/** PATCH body fragment: employee fields + `permission_ids` for admin-employees API. */
export function buildAdminEmployeePermissionPatchPayload(
  row,
  permissionIds,
  overrides = {}
) {
  const raw = row?.original ?? row ?? {};
  const first = String(row?.first_name ?? raw.first_name ?? "").trim();
  const last = String(row?.last_name ?? raw.last_name ?? "").trim();
  const email = String(row?.email ?? raw.email ?? "").trim();
  const mobile =
    row?.mobile ??
    raw.phone_number ??
    raw.phone ??
    raw.mobile ??
    "";
  const deptRaw = row?.department ?? raw.department;
  const department =
    deptRaw != null && String(deptRaw).trim() !== ""
      ? String(deptRaw).trim().toLowerCase()
      : null;
  const designationRaw =
    overrides.designation ??
    row?.designation ??
    raw.designation ??
    null;
  const designation =
    designationRaw != null && String(designationRaw).trim() !== ""
      ? String(designationRaw).trim().toLowerCase()
      : null;
  const role_ids = Array.isArray(row?.role_ids)
    ? row.role_ids
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n))
    : Array.isArray(raw.role_ids)
      ? raw.role_ids
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n))
      : [];

  const activeExplicitFalse =
    row?.status === "Blocked" ||
    raw.is_active === false ||
    raw.is_active === 0 ||
    raw.is_active === "false";

  return {
    first_name: first,
    last_name: last,
    email,
    phone_number: mobile != null ? String(mobile).trim() : "",
    isd_code: overrides.isd_code ?? "+91",
    is_active: !activeExplicitFalse,
    department,
    designation,
    role_ids,
    permission_ids: Array.isArray(permissionIds)
      ? permissionIds
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n))
      : [],
    ...overrides,
  };
}

export function isAdminEmployeeSession(user, accessToken) {
  const jwt = decodeJwtPayload(accessToken);
  const userType = String(
    user?.user_type || jwt?.user_type || ""
  ).toLowerCase();
  return (
    userType === "admin_employee" ||
    userType.includes("admin_employee") ||
    userType === "employee"
  );
}

async function tryPermissionGet(axiosGet, url, cfg = {}) {
  try {
    const res = await axiosGet(url, cfg);
    const list = normalizePermissionObjects(
      extractPermissionListFromPayload(res.data)
    );
    if (list.length > 0) return list;

    const row = res?.data?.data ?? res?.data;
    if (row && typeof row === "object") {
      return normalizePermissionObjects(row.permissions ?? []);
    }
  } catch {
    /* next source */
  }
  return [];
}

/**
 * GET /api/v1/admin-employee/employee-permissions/
 */
export async function fetchEmployeePermissions({
  accessToken,
  userId,
  userEmail,
  employeeId,
  axiosGet,
  forAxiosInstance = false,
}) {
  const url = resolveEmployeePermissionsUrl(forAxiosInstance);
  const paramSets = [
    userId != null && userEmail
      ? { user_id: userId, email: userEmail }
      : null,
    employeeId != null ? { employee_id: employeeId } : null,
    userId != null ? { user_id: userId } : null,
    employeeId != null ? { id: employeeId } : null,
    null,
  ];

  const getter =
    axiosGet ||
    ((requestUrl, cfg) => {
      const headers = { ...(cfg?.headers || {}) };
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
      return axios.get(requestUrl, { ...cfg, headers });
    });

  for (const params of paramSets) {
    const requestConfig = {};
    if (accessToken) {
      requestConfig.headers = { Authorization: `Bearer ${accessToken}` };
    }
    if (params) requestConfig.params = params;
    const list = await tryPermissionGet(getter, url, requestConfig);
    if (list.length > 0) return list;
  }

  return [];
}

/**
 * Fetch UI permissions for the logged-in user (legacy fallback).
 */
export async function fetchUiPermissionsForUser({
  accessToken,
  userId,
  userEmail,
  axiosGet,
}) {
  if (!accessToken && !axiosGet) return [];

  const permUrl = config.admin_user.uiPermissions;
  const requestConfig = {};
  if (accessToken) {
    requestConfig.headers = { Authorization: `Bearer ${accessToken}` };
  }
  if (userId && userEmail) {
    requestConfig.params = { user_id: userId, email: userEmail };
  }

  const getter =
    axiosGet ||
    ((url, cfg) => {
      const headers = { ...(cfg?.headers || {}) };
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
      return axios.get(url, { ...cfg, headers });
    });

  return tryPermissionGet(getter, permUrl, requestConfig);
}

/**
 * Resolve sidebar permissions: `permission_ids` + catalog → profile → legacy UI API.
 */
export async function resolveUserPermissionsForSession({
  user,
  accessToken,
  isSuperuser,
  axiosGet,
}) {
  if (isSuperuser) return [];

  const userId =
    user?.id || user?.user_id || user?._id || user?.pk;
  const userEmail = user?.email || user?.email_address;
  const employeeId =
    user?.employee_id ??
    user?.admin_employee_id ??
    user?.admin_employee?.id ??
    (isAdminEmployeeSession(user, accessToken) ? userId : null);

  const getter =
    axiosGet ||
    ((requestUrl, cfg) => {
      const headers = { ...(cfg?.headers || {}) };
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
      return axios.get(requestUrl, { ...cfg, headers });
    });

  let permissionIds = Array.isArray(user?.permission_ids)
    ? user.permission_ids
    : Array.isArray(user?.admin_employee?.permission_ids)
      ? user.admin_employee.permission_ids
      : [];

  if (permissionIds.length === 0 && employeeId != null) {
    try {
      const detailRes = await getter(config.employees.detail(employeeId));
      const row = detailRes?.data?.data ?? detailRes?.data ?? {};
      if (Array.isArray(row.permission_ids)) {
        permissionIds = row.permission_ids;
      }
    } catch {
      /* profile fetch optional */
    }
  }

  if (permissionIds.length > 0) {
    const catalog = await fetchEmployeePermissionCatalog(getter);
    const fromIds = catalogPermissionIdsToSessionPermissions(
      permissionIds,
      catalog
    );
    if (fromIds.length > 0) return fromIds;
  }

  const fromProfile = normalizePermissionObjects(user?.permissions);
  if (fromProfile.length > 0) return fromProfile;

  return fetchUiPermissionsForUser({
    accessToken,
    userId,
    userEmail,
    axiosGet: getter,
  });
}

/**
 * Load permissions for admin permission modal — never throws; tries multiple sources.
 */
export async function fetchEmployeePermissionsForModal({
  userId,
  userEmail,
  employeeId,
  axiosGet,
  profilePermissions,
  permissionIdsFromRow,
  stubKeyToId = {},
}) {
  const catalog = await fetchEmployeePermissionCatalog(axiosGet, true);
  const modulesForModal = buildPermissionModulesFromCatalog(catalog);
  const modalUserId = userId ?? employeeId;

  let list = normalizePermissionObjects(profilePermissions || []);

  if (list.length === 0) {
    list = await fetchEmployeePermissions({
      userId,
      userEmail,
      employeeId,
      axiosGet,
      forAxiosInstance: true,
    });
  }

  if (list.length === 0 && (employeeId != null || userId != null)) {
    const detailId = employeeId ?? userId;
    try {
      const detailRes = await axiosGet(config.employees.detail(detailId));
      const row = detailRes?.data?.data ?? detailRes?.data ?? {};
      const pid = Array.isArray(row.permission_ids) ? row.permission_ids : [];
      if (pid.length > 0) {
        let byKeyFromIds = permissionsListToModalByKey([], modalUserId);
        byKeyFromIds = mergePermissionIdsIntoModalByKey(
          byKeyFromIds,
          pid,
          catalog,
          stubKeyToId,
          modulesForModal,
          modalUserId
        );
        return { byKey: byKeyFromIds, catalog, modulesForModal };
      }
      list = normalizePermissionObjects(row.permissions ?? []);
    } catch {
      list = await tryPermissionGet(
        axiosGet,
        config.employees.detail(detailId)
      );
    }
  }

  if (list.length === 0 && userId != null && userEmail) {
    list = await tryPermissionGet(axiosGet, config.admin_user.permissionupdate, {
      params: { user_id: userId, email: userEmail },
    });
  }

  let byKey = permissionsListToModalByKey(list, modalUserId);

  if (Array.isArray(permissionIdsFromRow) && permissionIdsFromRow.length > 0) {
    byKey = mergePermissionIdsIntoModalByKey(
      byKey,
      permissionIdsFromRow,
      catalog,
      stubKeyToId,
      modulesForModal,
      modalUserId
    );
  }

  return { byKey, catalog, modulesForModal };
}
